import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  loggerActivationId,
  type LoggerActivationId,
} from './LoggerActivation.js'
import { orderId, type OrderId } from './Order.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.contract_journal_entry_ */
export type ContractJournalEntryId = string & {
  __brand: 'ContractJournalEntryId'
}

/** Represents the table public.contract_journal_entry_ */
export default interface ContractJournalEntryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ContractJournalEntryId,
    ContractJournalEntryId | undefined,
    ContractJournalEntryId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  orderId: ColumnType<OrderId, OrderId, OrderId>

  /** Database type: pg_catalog.uuid */
  loggerActivationId: ColumnType<
    LoggerActivationId | null,
    LoggerActivationId | null,
    LoggerActivationId | null
  >

  /** Database type: pg_catalog.text */
  erpnextJournalEntryId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  daysRecognized: ColumnType<number | null, number | null, number | null>
}

export type ContractJournalEntry = Selectable<ContractJournalEntryTable>

export type ContractJournalEntryInitializer =
  Insertable<ContractJournalEntryTable>

export type ContractJournalEntryMutator = Updateable<ContractJournalEntryTable>

export const contractJournalEntryId =
  z.string() as unknown as z.Schema<ContractJournalEntryId>

export const contractJournalEntry = z.object({
  id: contractJournalEntryId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  orderId: orderId,
  loggerActivationId: loggerActivationId.nullable().optional(),
  erpnextJournalEntryId: z.string().nullable().optional(),
  daysRecognized: z.number().nullable().optional(),
}) as unknown as z.Schema<ContractJournalEntry>

export const contractJournalEntryInitializer = z.object({
  id: contractJournalEntryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  orderId: orderId,
  loggerActivationId: loggerActivationId.optional().nullable(),
  erpnextJournalEntryId: z.string().optional().nullable(),
  daysRecognized: z.number().optional().nullable(),
}) as unknown as z.Schema<ContractJournalEntryInitializer>

export const contractJournalEntryMutator = z.object({
  id: contractJournalEntryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  orderId: orderId.optional(),
  loggerActivationId: loggerActivationId.optional().nullable(),
  erpnextJournalEntryId: z.string().optional().nullable(),
  daysRecognized: z.number().optional().nullable(),
}) as unknown as z.Schema<ContractJournalEntryMutator>
